import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { LocationPicker } from "@/components/LocationPicker";
import {
  MapPin,
  Calendar,
  User,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  ImageIcon,
  ArrowLeft,
  FileText,
  Phone,
  Mail,
  Loader2,
  Archive,
  RotateCcw,
  Lightbulb,
  PlayIcon,
  Search,
  X,
  MessageSquare,
} from "lucide-react";
import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { loadGoogleMaps } from "@/lib/googleMapsLoader";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDebounce } from "@/hooks/useDebounce";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import {
  useGetOcurrenceByProtocol,
  useGetCategories,
  useGetSubCategoriesByCategoryId,
  useGetSectors,
  useUpdateOccurrence,
  useUnificateScreening,
  useUpdateAnalysisStatus,
  useSearchOccurrencesByProtocol,
} from "@/hooks/useOccurrences";
import { useGetRegionals } from "@/hooks/useRegionals";
import { useGetMessageTemplates } from "@/hooks/useMessageTemplate";
import { useGetConfiguration } from "@/hooks/useConfiguration";
import { Textarea } from "@/components/ui/textarea";
import { HeaderPage } from "@/components/HeaderPage";
import { Loading } from "@/components/Loading";

const reasonsForReturn = [
  { value: "IMPRECISE_LOCATION", label: "Localização imprecisa ou incorreta" },
  {
    value: "INSUFFICIENT_DESCRIPTION",
    label: "Descrição insuficiente do problema",
  },
  { value: "MISSING_EVIDENCE", label: "Fotos/evidências necessárias" },
  { value: "INVALID_CONTACT", label: "Dados de contato inválidos" },
  { value: "CONTRADICTORY_INFO", label: "Informações contraditórias" },
  { value: "OTHER", label: "Outro" },
];

const reasonsForArchive = [
  { value: "DUPLICATE", label: "Ocorrência duplicada" },
  { value: "OUT_OF_SCOPE", label: "Fora da competência municipal" },
  { value: "FALSE_INFO", label: "Informações falsas ou inconsistentes" },
  { value: "ALREADY_RESOLVED", label: "Problema já resolvido" },
  { value: "INAPPROPRIATE_CONTENT", label: "Conteúdo ofensivo ou inadequado" },
  {
    value: "NOT_PUBLIC_SERVICE",
    label: "Solicitação não caracteriza serviço público",
  },
  { value: "OTHER", label: "Outro" },
];

const getDefaultPriorityConfig = () => ({
  LOW: {
    label: "Baixa",
    color: "bg-blue-50 text-blue-600 border-blue-500",
    description: "Pode aguardar até 15 dias",
    days: 15,
  },
  MEDIUM: {
    label: "Média",
    color: "bg-yellow-50 text-yellow-600 border-yellow-500",
    description: "Resolver em até 5 dias",
    days: 5,
  },
  HIGH: {
    label: "Alta",
    color: "bg-orange-50 text-orange-600 border-orange-500",
    description: "Resolver em até 2 dias",
    days: 2,
  },
  URGENT: {
    label: "Crítica",
    color: "bg-red-50 text-red-600 border-red-500",
    description: "Resolver em até 1 dia",
    days: 1,
  },
});

const statusConfig: Record<string, { label: string; variant: any }> = {
  AWAITING_ANALYSIS: { label: "Aguardando Análise", variant: "outline" },
  IN_PROGRESS: { label: "Em Andamento", variant: "secondary" },
  RESOLVED: { label: "Resolvida", variant: "default" },
  // REJECTED: { label: "Rejeitada", variant: "destructive" },
  CANCELLED: { label: "Cancelada", variant: "destructive" },
  REOPENED: { label: "Reaberta", variant: "outline" },
  RETURNED: { label: "Devolvida", variant: "warning" },
  AWAITING_REANALYSIS: { label: "Aguardando Reanálise", variant: "secondary" },
  FILED: { label: "Arquivada", variant: "alert" },
};

// Função para renderizar texto com markdown básico (negrito)
const renderMarkdownText = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const boldText = part.slice(2, -2);
      return (
        <strong key={index} className="font-bold">
          {boldText}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

export const Screening = () => {
  const { protocol } = useParams<{ protocol: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Detectar se veio da visualização ou da fila de triagem
  const fromView = location.state?.from === "view";
  const allowTriageDecision = !fromView; // Só permite decisão de triagem se não veio da view

  const { data: occurrence, isLoading } = useGetOcurrenceByProtocol(protocol!);
  const { data: categoriesData } = useGetCategories();
  const { data: sectorsData } = useGetSectors();
  const { data: settingsData } = useGetConfiguration();
  const updateAnalysisStatus = useUpdateAnalysisStatus();

  // Configurar prioridades dinamicamente baseado em settings
  const priorityConfig = useMemo(() => {
    const defaultConfig = getDefaultPriorityConfig();

    if (!settingsData) return defaultConfig;

    return {
      LOW: {
        ...defaultConfig.LOW,
        days: settingsData.slaLowDays || defaultConfig.LOW.days,
        description: `Resolver em até ${settingsData.slaLowDays || defaultConfig.LOW.days} dias`,
      },
      MEDIUM: {
        ...defaultConfig.MEDIUM,
        days: settingsData.slaMediumDays || defaultConfig.MEDIUM.days,
        description: `Resolver em até ${settingsData.slaMediumDays || defaultConfig.MEDIUM.days} dias`,
      },
      HIGH: {
        ...defaultConfig.HIGH,
        days: settingsData.slaHighDays || defaultConfig.HIGH.days,
        description: `Resolver em até ${settingsData.slaHighDays || defaultConfig.HIGH.days} dias`,
      },
      URGENT: {
        ...defaultConfig.URGENT,
        days: settingsData.slaCriticalDays || defaultConfig.URGENT.days,
        description: `Resolver em até ${settingsData.slaCriticalDays || defaultConfig.URGENT.days} dia(s)`,
      },
    };
  }, [settingsData]);

  // Marcar como "em análise" ao entrar e desmarcar ao sair
  useEffect(() => {
    if (!occurrence?.id) return;

    // Marcar como em análise ao entrar
    updateAnalysisStatus.mutate({
      occurrenceId: occurrence.id,
      isBeingAnalyzed: true,
    });

    // Desmarcar ao desmontar o componente
    return () => {
      updateAnalysisStatus.mutate({
        occurrenceId: occurrence.id,
        isBeingAnalyzed: false,
      });
    };
  }, [occurrence?.id]);

  // Desmarcar se o usuário fechar a aba/navegador
  useEffect(() => {
    if (!occurrence?.id) return;

    const handleBeforeUnload = () => {
      // Usar sendBeacon para garantir que a requisição seja enviada mesmo ao fechar
      const userDataString = localStorage.getItem("duas-vidas-user");
      const userData = userDataString ? JSON.parse(userDataString) : null;
      const token = userData?.token;

      if (token) {
        const blob = new Blob([JSON.stringify({ isBeingAnalyzed: false })], {
          type: "application/json",
        });
        navigator.sendBeacon(
          `${import.meta.env.VITE_BASE_URL}/occurrences/${occurrence.id}/analysis-status`,
          blob,
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [occurrence?.id]);

  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState("");
  const [selectedPriority, setSelectedPriority] = useState<string>(
    occurrence?.userPerceivedUrgency || "MEDIUM",
  );
  const [selectedSectorId, setSelectedSectorId] = useState("");
  const [selectedRegionalId, setSelectedRegionalId] = useState("");

  // Buscar regionais do setor selecionado
  const { data: regionalsData } = useGetRegionals(selectedSectorId);

  const [notes, setNotes] = useState("");
  const [action, setAction] = useState<"approve" | "return" | "archive">(
    "approve",
  );
  const [coordinates, setCoordinates] = useState<[number, number] | null>(null);
  const [newAddress, setNewAddress] = useState<string>("");
  const [newFullAddress, setNewFullAddress] = useState<any>(null);
  const [locationManuallyChanged, setLocationManuallyChanged] = useState(false);

  // RN-003: Justificativa obrigatória para prioridade Crítica
  const [criticalPriorityJustification, setCriticalPriorityJustification] =
    useState("");

  const [isAlreadyForwarded, setIsAlreadyForwarded] = useState(false);

  const [reasonId, setReasonId] = useState("");
  const [actionDescription, setActionDescription] = useState("");
  const [messageTemplateId, setMessageTemplateId] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [duplicateProtocol, setDuplicateProtocol] = useState("");
  const [protocolSearchTerm, setProtocolSearchTerm] = useState("");
  const [showProtocolSuggestions, setShowProtocolSuggestions] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [editableDescription, setEditableDescription] = useState("");
  const [editableAddress, setEditableAddress] = useState("");
  const [locationJustification, setLocationJustification] = useState("");
  const [hasAddressBeenManuallyChanged, setHasAddressBeenManuallyChanged] =
    useState(false);

  // SLA - Prazo de Solução
  const [slaValue, setSlaValue] = useState<number>(0);
  const [slaUnit, setSlaUnit] = useState<"HOURS" | "DAYS">("DAYS");
  const [calculatedDeadline, setCalculatedDeadline] = useState<any>(null);
  const [isCalculatingSla, setIsCalculatingSla] = useState(false);
  const [slaInitialized, setSlaInitialized] = useState(false);

  // Sincronizar SLA inicial com a prioridade pré-selecionada
  useEffect(() => {
    if (slaInitialized) return;
    const key = selectedPriority as keyof typeof priorityConfig;
    const days = priorityConfig[key]?.days;
    if (days) {
      setSlaValue(days);
      setSlaUnit("DAYS");
      setSlaInitialized(true);
    }
  }, [selectedPriority, priorityConfig, slaInitialized]);

  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".protocol-search-container")) {
        setShowProtocolSuggestions(false);
      }
    };

    if (showProtocolSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showProtocolSuggestions]);

  // Debounce dos valores de SLA para não disparar API a cada keystroke
  const debouncedSlaValue = useDebounce(slaValue, 600);
  const debouncedSlaUnit = useDebounce(slaUnit, 600);

  // Calcular SLA automaticamente quando valores debounced mudarem
  useEffect(() => {
    const calculateSla = async () => {
      if (!debouncedSlaValue || debouncedSlaValue <= 0) {
        setCalculatedDeadline(null);
        return;
      }

      setIsCalculatingSla(true);
      try {
        const token = localStorage.getItem("duas-vidas-token");

        const response = await fetch(
          `${import.meta.env.VITE_BASE_URL}/occurrences/calculate-sla`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              slaValue: debouncedSlaValue,
              slaUnit: debouncedSlaUnit,
              urgency: selectedPriority,
            }),
          },
        );

        if (response.ok) {
          const data = await response.json();
          setCalculatedDeadline(data);
        }
      } catch (error) {
        console.error("Erro ao calcular SLA:", error);
      } finally {
        setIsCalculatingSla(false);
      }
    };

    calculateSla();
  }, [debouncedSlaValue, debouncedSlaUnit, selectedPriority]);

  const { data: subCategoriesData } = useGetSubCategoriesByCategoryId(
    selectedCategoryId || "",
  );
  const { data: templatesData } = useGetMessageTemplates();

  // Normaliza templates para sempre ser um array
  const templates = Array.isArray(templatesData)
    ? templatesData
    : templatesData?.data || templatesData?.templates || [];

  const updateOccurrence = useUpdateOccurrence();
  const unificateScreening = useUnificateScreening();

  // Função para preencher mensagem ao selecionar template
  const handleTemplateChange = (templateId: string) => {
    setMessageTemplateId(templateId);
    const selectedTemplate = templates.find((t: any) => t.id === templateId);
    if (selectedTemplate) {
      setCustomMessage(selectedTemplate.content);
    }
  };
  const { data: protocolSearchResults, isLoading: isSearchingProtocol } =
    useSearchOccurrencesByProtocol(protocolSearchTerm);

  const handleViewMedia = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const handleViewVideo = useCallback((videoUrl: string) => {
    setSelectedVideo(videoUrl);
    setVideoDialogOpen(true);
  }, []);

  const handleLocationChange = useCallback((location: {
    coordinates: [number, number];
    address: string;
    fullAddress: any;
  }) => {
    setCoordinates(location.coordinates);
    setNewAddress(location.address);
    setNewFullAddress(location.fullAddress);
    setLocationManuallyChanged(true);
    setHasAddressBeenManuallyChanged(true);
  }, []);

  const imageSlides = useMemo(
    () =>
      occurrence?.media
        ?.filter((m: any) => m.type === "IMAGE")
        .map((media: any) => ({
          src: `${import.meta.env.VITE_BUCKET_URL}${media.key}`,
        })) || [],
    [occurrence?.media],
  );

  useEffect(() => {
    if (occurrence) {
      setSelectedCategoryId(occurrence.category.id);
      setSelectedPriority(occurrence.userPerceivedUrgency || "MEDIUM");
      setSelectedSectorId(occurrence.sector?.id || "");
      setEditableDescription(occurrence.description || "");
      setEditableAddress(occurrence.address || "");

      // RN-007: Verificar se ocorrência já foi encaminhada
      const forwardedStatuses = ["IN_PROGRESS", "RESOLVED", "REJECTED"];
      setIsAlreadyForwarded(forwardedStatuses.includes(occurrence.status));
    }
  }, [occurrence]);

  useEffect(() => {
    if (occurrence && subCategoriesData) {
      setSelectedSubcategoryId(occurrence.subcategory.id);

      // RN-004 e RN-005: Auto-preencher setor e prioridade padrão da subcategoria
      const subcategory = subCategoriesData.find(
        (sub: any) => sub.id === occurrence.subcategory.id,
      );
      if (subcategory) {
        // RN-004: Preencher setor padrão se disponível
        if (subcategory.defaultSectorId && !selectedSectorId) {
          setSelectedSectorId(subcategory.defaultSectorId);
        }
        // RN-005: Sugerir prioridade padrão se disponível
        if (subcategory.defaultPriority && selectedPriority === "MEDIUM") {
          setSelectedPriority(subcategory.defaultPriority);
        }
      }
    }
  }, [occurrence, subCategoriesData]);

  useEffect(() => {
    const geocodeAddress = () => {
      if (!occurrence?.address) return;

      loadGoogleMaps(() => {
        if (!window.google?.maps) {
          setCoordinates([-3.7327, -38.527]);
          return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { address: occurrence.address },
          (results: any, status: string) => {
            if (status === "OK" && results?.[0]?.geometry?.location) {
              const location = results[0].geometry.location;
              setCoordinates([location.lat(), location.lng()]);
            } else {
              setCoordinates([-3.7327, -38.527]);
            }
          },
        );
      });
    };

    if (occurrence?.latitude && occurrence?.longitude) {
      // Garantir que são números válidos
      const lat = parseFloat(String(occurrence.latitude));
      const lng = parseFloat(String(occurrence.longitude));
      if (!isNaN(lat) && !isNaN(lng)) {
        setCoordinates([lat, lng]);
      } else {
        geocodeAddress();
      }
    } else {
      geocodeAddress();
    }
  }, [occurrence?.address, occurrence?.latitude, occurrence?.longitude]);

  const handleConfirm = async () => {
    // Validações apenas se permitir decisão de triagem
    if (allowTriageDecision) {
      if (action === "approve" && !selectedSectorId) {
        toast.error("Setor obrigatório");
        return;
      }

      // Validar SLA
      if (action === "approve") {
        if (!slaValue || slaValue <= 0) {
          toast.error("Informe o prazo de solução (valor maior que 0)");
          return;
        }
        if (!slaUnit) {
          toast.error(
            "Selecione a unidade do prazo de solução (Horas ou Dias)",
          );
          return;
        }
      }
    }

    // Validar justificativa se endereço foi alterado manualmente
    const addressChanged =
      hasAddressBeenManuallyChanged &&
      ((newAddress != "" && newAddress != occurrence?.address) ||
        editableAddress != occurrence?.address ||
        (coordinates &&
          (coordinates[0] != occurrence?.latitude ||
            coordinates[1] != occurrence?.longitude)));

    if (addressChanged && !locationJustification) {
      toast.error("Justificativa obrigatória ao alterar a localização");
      return;
    }

    // RN-003: Validar justificativa obrigatória para prioridade Crítica
    if (
      allowTriageDecision &&
      selectedPriority === "URGENT" &&
      !criticalPriorityJustification.trim()
    ) {
      toast.error(
        "Justificativa obrigatória para prioridade Crítica (mínimo 20 caracteres)",
      );
      return;
    }

    if (
      allowTriageDecision &&
      selectedPriority === "URGENT" &&
      criticalPriorityJustification.length < 20
    ) {
      toast.error(
        "Justificativa para prioridade Crítica deve ter no mínimo 20 caracteres",
      );
      return;
    }

    if (allowTriageDecision && action === "return") {
      if (!reasonId) {
        toast.error("Selecione o motivo da devolução");
        return;
      }
      if (!actionDescription) {
        toast.error("Preencha a descrição da devolução");
        return;
      }
    }

    if (allowTriageDecision && action === "archive") {
      if (!reasonId) {
        toast.error("Selecione o motivo do arquivamento");
        return;
      }
      if (!actionDescription) {
        toast.error("Preencha a descrição do arquivamento");
        return;
      }
      if (actionDescription.length < 30) {
        toast.error("A descrição deve ter no mínimo 30 caracteres");
        return;
      }
      if (reasonId === "DUPLICATE" && !duplicateProtocol) {
        toast.error("Informe o protocolo da ocorrência original");
        return;
      }
    }

    try {
      const updateData: any = {
        categoryId: selectedCategoryId,
        subcategoryId: selectedSubcategoryId,
        priority: selectedPriority,
        sectorId: selectedSectorId || undefined,
        regionalId: selectedRegionalId || undefined, // Adicionar regionalId se existir
        description: editableDescription,
        address: newAddress || editableAddress,
        // RN-009: Flag para encaminhamento imediato de prioridade crítica
        urgentForwarding: selectedPriority === "URGENT",
      };

      // Se descrição foi alterada pela triagem, enviar descrição original para snapshot
      if (editableDescription !== occurrence?.description) {
        updateData.originalDescription = occurrence?.description;
      }

      // RN-003: Adicionar justificativa de prioridade crítica
      if (selectedPriority === "URGENT" && criticalPriorityJustification) {
        updateData.criticalPriorityJustification =
          criticalPriorityJustification;
      }

      // Adicionar justificativa e coordenadas se endereço foi alterado
      const addressChanged =
        (newAddress && newAddress !== occurrence?.address) ||
        editableAddress !== occurrence?.address;
      if (addressChanged) {
        if (locationJustification) {
          updateData.justification = locationJustification;
        }
        // Adicionar coordenadas se foram alteradas
        if (coordinates) {
          updateData.latitude = coordinates[0];
          updateData.longitude = coordinates[1];
        }
      }

      await updateOccurrence.mutateAsync({
        protocol: occurrence.protocol,
        data: updateData,
      });

      // Só processar decisão de triagem se permitido (veio da fila de triagem)
      if (allowTriageDecision) {
        const screeningData: any = {
          id: occurrence.id,
          status:
            action === "approve"
              ? "APROVADO"
              : action === "return"
                ? "DEVOLVIDO"
                : "ARQUIVADO",
        };

        if (action === "approve") {
          screeningData.observation = notes;
          screeningData.slaValue = slaValue;
          screeningData.slaUnit = slaUnit;
        } else if (action === "return") {
          screeningData.reason = reasonId;
          screeningData.reasonDescription = actionDescription;
          if (customMessage) {
            screeningData.personalizedMessage = customMessage;
          }
        } else if (action === "archive") {
          screeningData.reason = reasonId;
          screeningData.reasonDescription = actionDescription;
          if (reasonId === "DUPLICATE") {
            screeningData.originalProtocol = duplicateProtocol;
          }
          if (customMessage) {
            screeningData.personalizedMessage = customMessage;
          }
        }

        await unificateScreening.mutateAsync(screeningData);

        const actionMessages = {
          approve: `Ocorrência ${protocol} aprovada e encaminhada com sucesso!${selectedPriority === "URGENT" ? " ⚠️ Prioridade CRÍTICA - Encaminhamento imediato!" : ""}`,
          return: `Ocorrência ${protocol} devolvida para o cidadão`,
          archive: `Ocorrência ${protocol} arquivada`,
        };
        toast.success(actionMessages[action]);

        // RN-009: Notificação adicional para prioridade crítica
        if (action === "approve" && selectedPriority === "URGENT") {
          toast.success("🚨 Supervisor notificado sobre ocorrência crítica!", {
            duration: 5000,
          });
        }
      } else {
        // Se veio da view, apenas edição
        toast.success(`Ocorrência ${protocol} atualizada com sucesso!`);
      }

      navigate("/occurrences");
    } catch (error: any) {
      console.error("Erro ao processar triagem:", error);
      
      // Extrair mensagem do backend
      const errorMessage = 
        error?.response?.data?.message || 
        error?.message || 
        "Erro ao processar triagem. Tente novamente.";
      
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return <Loading label="Carregando ocorrência.." />;
  }

  if (!occurrence) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-lg font-medium text-gray-700">
          Ocorrência não encontrada
        </p>
        <Button
          onClick={() => navigate("/occurrences")}
          variant="outline"
          className="cursor-pointer"
        >
          Voltar para Ocorrências
        </Button>
      </div>
    );
  }

  return (
    <>
      <HeaderPage
        title={
          allowTriageDecision ? "Triagem de Ocorrência" : "Editar Ocorrência"
        }
        description={
          allowTriageDecision
            ? "Classificar e encaminhar ocorrência"
            : "Editar informações da ocorrência"
        }
      />
      <div className="flex items-center justify-between mb-4 mt-5">
        <Button
          className="cursor-pointer"
          variant="outline"
          onClick={() => navigate("/occurrences")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para lista
        </Button>
      </div>

      {/* RN-007: Alerta quando ocorrência já foi encaminhada */}
      {isAlreadyForwarded && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-orange-800 mb-1">
                Ocorrência Já Encaminhada
              </p>
              <p className="text-xs text-orange-700">
                Esta ocorrência já foi encaminhada ao setor responsável.
                Alterações na classificação requerem justificativa obrigatória.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 w-full">
          <Card className="bg-white w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-status-triagem" />
                Formulário de Triagem
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">
                    1
                  </span>
                  <h3 className="text-sm font-semibold">Classificação</h3>
                </div>

                {/* RN-002: Exibir classificação original do cidadão */}
                {occurrence.originalCategoryId &&
                  (occurrence.originalCategoryId !== selectedCategoryId ||
                    occurrence.originalSubcategoryId !==
                      selectedSubcategoryId) && (
                    <div className="pl-8 mb-3">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <Lightbulb className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-amber-800 mb-1">
                              Classificação Original do Cidadão
                            </p>
                            <div className="text-xs text-amber-700 space-y-0.5">
                              <p>
                                <strong>Categoria:</strong>{" "}
                                {occurrence.originalCategory?.name ||
                                  "Não informada"}
                              </p>
                              <p>
                                <strong>Subcategoria:</strong>{" "}
                                {occurrence.originalSubcategory?.name ||
                                  "Não informada"}
                              </p>
                              {occurrence.originalPriority && (
                                <p>
                                  <strong>Prioridade sugerida:</strong>{" "}
                                  {
                                    priorityConfig[occurrence.originalPriority]
                                      ?.label
                                  }
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-8">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Categoria</Label>
                    <Select
                      value={selectedCategoryId}
                      onValueChange={(value) => {
                        setSelectedCategoryId(value);
                        setSelectedSubcategoryId("");
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Selecione a categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categoriesData?.data?.map((cat: any) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Subcategoria</Label>
                    <Select
                      value={selectedSubcategoryId}
                      onValueChange={(value) => {
                        setSelectedSubcategoryId(value);
                        // RN-004 e RN-005: Auto-preencher baseado na subcategoria
                        const subcategory = subCategoriesData?.find(
                          (sub: any) => sub.id === value,
                        );
                        if (subcategory) {
                          if (subcategory.defaultSectorId) {
                            setSelectedSectorId(subcategory.defaultSectorId);
                            toast.success(
                              `Setor padrão selecionado: ${subcategory.defaultSector?.name || ""}`,
                            );
                          }
                          if (subcategory.defaultPriority) {
                            setSelectedPriority(subcategory.defaultPriority);
                            toast.success(
                              `Prioridade padrão sugerida: ${priorityConfig[subcategory.defaultPriority]?.label}`,
                            );
                          }
                        }
                      }}
                      disabled={!selectedCategoryId}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Selecione a subcategoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {subCategoriesData?.map((sub: any) => (
                          <SelectItem key={sub.id} value={sub.id}>
                            <div className="flex items-center gap-2">
                              {sub.name}
                              {(sub.defaultSectorId || sub.defaultPriority) && (
                                <Badge
                                  variant="secondary"
                                  className="text-[10px] px-1 py-0"
                                >
                                  Auto
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">
                    2
                  </span>
                  <h3 className="text-sm font-semibold">Prioridade</h3>
                </div>

                <RadioGroup
                  value={selectedPriority}
                  onValueChange={(value) => {
                    setSelectedPriority(value);
                    // RN-003: Limpar justificativa se não for mais URGENT
                    if (value !== "URGENT") {
                      setCriticalPriorityJustification("");
                    }
                    // Auto-preencher SLA baseado na prioridade selecionada
                    const key = value as keyof typeof priorityConfig;
                    const priorityDays = priorityConfig[key]?.days;
                    if (priorityDays) {
                      setSlaValue(priorityDays);
                      setSlaUnit("DAYS");
                    }
                  }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 pl-8"
                >
                  {Object.entries(priorityConfig).map(([key, config]) => (
                    <div key={key}>
                      <RadioGroupItem
                        value={key}
                        id={`priority-${key}`}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={`priority-${key}`}
                        className={`flex flex-col items-center justify-center rounded-lg border-2 p-4 cursor-pointer transition-all h-full shadow-sm hover:shadow-md
                          ${selectedPriority === key ? config.color + " border-current shadow-md" : "border-muted hover:border-muted-foreground/50 bg-white"}
                        `}
                      >
                        <span className="font-bold text-base">
                          {config.label}
                        </span>
                        <span className="text-xs text-center mt-1 opacity-80">
                          {config.description}
                        </span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>

                {/* RN-003: Campo de justificativa obrigatória para prioridade Crítica */}
                {selectedPriority === "URGENT" && (
                  <div className="pl-8">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-600 mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-red-800 mb-1">
                            Justificativa Obrigatória para Prioridade Crítica
                          </p>
                          <p className="text-xs text-red-700 mb-3">
                            Explique detalhadamente por que esta ocorrência
                            requer atendimento crítico/urgente (mínimo 20
                            caracteres). Esta justificativa será enviada ao
                            supervisor para análise.
                          </p>
                          <Textarea
                            value={criticalPriorityJustification}
                            onChange={(e) =>
                              setCriticalPriorityJustification(e.target.value)
                            }
                            placeholder="Ex: Ocorrência envolve risco iminente à vida, vazamento de gás com risco de explosão, falta de água em hospital, etc."
                            className="min-h-[100px] bg-white border-red-300 focus:border-red-500"
                          />
                          <p className="text-xs text-red-600 mt-2">
                            {criticalPriorityJustification.length}/20 caracteres
                            {criticalPriorityJustification.length >= 20 && (
                              <span className="text-green-600 ml-2">✓</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Separator />



              {isAlreadyForwarded &&
                (selectedCategoryId !== occurrence?.category.id ||
                  selectedSubcategoryId !== occurrence?.subcategory.id ||
                  selectedPriority !== occurrence?.priority) && <Separator />}

              {/* Etapa 3 - Encaminhamento */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">
                    3
                  </span>
                  <h3 className="text-sm font-semibold">Encaminhamento</h3>
                </div>

                <div className="pl-8 space-y-3">
                  <div className="space-y-1.5 max-w-md">
                    <Label className="text-xs font-medium">
                      Setor Responsável
                    </Label>
                    <Select
                      value={selectedSectorId}
                      onValueChange={(value) => {
                        setSelectedSectorId(value);
                        setSelectedRegionalId(""); // Limpar regional ao trocar setor
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Selecione o setor responsável" />
                      </SelectTrigger>
                      <SelectContent>
                        {sectorsData?.data?.map((sector: any) => (
                          <SelectItem key={sector.id} value={sector.id}>
                            {sector.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Select de Regional */}
                  {selectedSectorId &&
                    regionalsData &&
                    regionalsData.length > 0 && (
                      <div className="space-y-1.5 max-w-md">
                        <Label className="text-xs font-medium">
                          Regional (Opcional)
                        </Label>
                        <Select
                          value={selectedRegionalId}
                          onValueChange={setSelectedRegionalId}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Selecione a regional" />
                          </SelectTrigger>
                          <SelectContent>
                            {regionalsData.map((regional: any) => (
                              <SelectItem key={regional.id} value={regional.id}>
                                {regional.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                </div>
              </div>

              <Separator />

              {/* Prazo de Solução (SLA) */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">
                    4
                  </span>
                  <h3 className="text-sm font-semibold">Prazo de Solução</h3>
                </div>

                <div className="pl-8 space-y-4">
                  <div className="grid grid-cols-2 gap-3 max-w-md">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        Valor <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        min="1"
                        value={slaValue || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || value === "0") {
                            setSlaValue(0);
                          } else {
                            setSlaValue(Number(value));
                          }
                        }}
                        placeholder="Ex: 48"
                        className="h-9"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">
                        Unidade <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={slaUnit}
                        onValueChange={(value: "HOURS" | "DAYS") =>
                          setSlaUnit(value)
                        }
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="HOURS">Horas</SelectItem>
                          <SelectItem value="DAYS">Dias</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Deadline Calculado */}
                  {isCalculatingSla && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                        <span className="text-xs text-gray-600">
                          Calculando prazo...
                        </span>
                      </div>
                    </div>
                  )}

                  {!isCalculatingSla && calculatedDeadline && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <Clock className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-blue-800">
                              Prazo Final Estimado
                            </p>
                            <p className="text-lg font-bold text-blue-900 mt-1">
                              {new Date(
                                calculatedDeadline.deadline,
                              ).toLocaleString("pt-BR", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                            {calculatedDeadline.calculationInfo && (
                              <div className="mt-2 text-xs text-blue-700 space-y-1">
                                <p>
                                  {calculatedDeadline.calculationInfo
                                    .isContinuous
                                    ? "⏰ Cálculo em horas corridas (24x7)"
                                    : `📅 Cálculo em horário comercial (${calculatedDeadline.calculationInfo.workShiftHours}h/dia)`}
                                </p>
                                {selectedPriority === "URGENT" && (
                                  <p className="font-semibold text-red-600">
                                    Prioridade Crítica - SLA em horas corridas
                                    (24x7)
                                  </p>
                                )}
                                {!calculatedDeadline.calculationInfo
                                  .isContinuous && (
                                  <p className="text-gray-600">
                                    ℹ️ Observação: Cálculo baseado em equipes
                                    com horário Integral
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Etapa 5 - Edição de Descrição e Endereço (ADMIN) */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">
                    5
                  </span>
                  <h3 className="text-sm font-semibold">
                    Editar Informações da Ocorrência
                  </h3>
                </div>

                <div className="pl-8 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      Descrição da Ocorrência *
                    </Label>
                    <Textarea
                      placeholder="Descrição detalhada da ocorrência..."
                      value={editableDescription}
                      onChange={(e: any) =>
                        setEditableDescription(e.target.value)
                      }
                      rows={4}
                      className="resize-none text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Endereço *</Label>
                    <Input
                      placeholder="Endereço da ocorrência..."
                      value={editableAddress}
                      onChange={(e) => {
                        setEditableAddress(e.target.value);
                        setHasAddressBeenManuallyChanged(true);
                      }}
                      className="text-sm"
                    />
                    <p className="text-xs text-gray-500">
                      💡 Você pode editar o endereço aqui ou ajustar a
                      localização no mapa abaixo
                    </p>
                  </div>

                  {/* Mostrar justificativa APENAS se endereço ou coordenadas mudaram manualmente */}
                  {hasAddressBeenManuallyChanged &&
                    (editableAddress != occurrence?.address ||
                      (coordinates &&
                        (coordinates[0] != occurrence?.latitude ||
                          coordinates[1] != occurrence?.longitude))) && (
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">
                          Justificativa da alteração de localização *
                        </Label>
                        <Select
                          value={locationJustification}
                          onValueChange={setLocationJustification}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Selecione a justificativa" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Localização imprecisa do GPS">
                              Localização imprecisa do GPS
                            </SelectItem>
                            <SelectItem value="Endereço incorreto informado pelo cidadão">
                              Endereço incorreto informado pelo cidadão
                            </SelectItem>
                            <SelectItem value="Ponto ajustado conforme fotos anexadas">
                              Ponto ajustado conforme fotos anexadas
                            </SelectItem>
                            <SelectItem value="Correção com base em conhecimento local">
                              Correção com base em conhecimento local
                            </SelectItem>
                            <SelectItem value="Outro">Outro</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-yellow-600">
                          ⚠️ Campo obrigatório ao alterar endereço ou
                          localização
                        </p>
                      </div>
                    )}
                </div>
              </div>

              <Separator />
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">
                    6
                  </span>
                  <h3 className="text-sm font-semibold">
                    Observações da Triagem
                  </h3>
                </div>

                <div className="pl-8">
                  <Textarea
                    placeholder="Adicione observações relevantes para o setor responsável, informações adicionais ou justificativas..."
                    value={notes}
                    onChange={(e: any) => setNotes(e.target.value)}
                    rows={3}
                    className="resize-none text-sm"
                  />
                </div>
              </div>

              {allowTriageDecision && <Separator />}

              {/* Etapa 7 - Decisão */}
              {allowTriageDecision && (
                <>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <span className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-medium">
                        7
                      </span>
                      <h3 className="text-lg font-semibold">Decisão Final</h3>
                    </div>

                    <RadioGroup
                      value={action}
                      onValueChange={(value) =>
                        setAction(value as "approve" | "return" | "archive")
                      }
                      className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-11"
                    >
                      <div>
                        <RadioGroupItem
                          value="approve"
                          id="action-approve"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="action-approve"
                          className={`flex items-center justify-center gap-2 rounded-lg border-2 p-4 cursor-pointer transition-all shadow-sm hover:shadow-md
                        ${action === "approve" ? "bg-green-50 text-green-600 border-green-500 shadow-md" : "border-muted hover:border-muted-foreground/50 bg-white"}
                      `}
                        >
                          <CheckCircle className="h-6 w-6" />
                          <div className="text-center">
                            <span className="font-bold block text-base">
                              Aprovar
                            </span>
                            <span className="text-xs opacity-80">
                              Enviar para o setor
                            </span>
                          </div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="return"
                          id="action-return"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="action-return"
                          className={`flex items-center justify-center gap-2 rounded-lg border-2 p-4 cursor-pointer transition-all shadow-sm hover:shadow-md
                        ${action === "return" ? "bg-yellow-50 text-yellow-600 border-yellow-500 shadow-md" : "border-muted hover:border-muted-foreground/50 bg-white"}
                      `}
                        >
                          <RotateCcw className="h-6 w-6" />
                          <div className="text-center">
                            <span className="font-bold block text-base">
                              Devolver
                            </span>
                            <span className="text-xs opacity-80">
                              Retorna ao cidadão
                            </span>
                          </div>
                        </Label>
                      </div>

                      <div>
                        <RadioGroupItem
                          value="archive"
                          id="action-archive"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="action-archive"
                          className={`flex items-center justify-center gap-2 rounded-lg border-2 p-4 cursor-pointer transition-all shadow-sm hover:shadow-md
                        ${action === "archive" ? "bg-gray-50 text-gray-600 border-gray-500 shadow-md" : "border-muted hover:border-muted-foreground/50 bg-white"}
                      `}
                        >
                          <Archive className="h-6 w-6" />
                          <div className="text-center">
                            <span className="font-bold block text-base">
                              Arquivar
                            </span>
                            <span className="text-xs opacity-80">
                              Arquiva a ocorrência
                            </span>
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Campos extras para Devolver */}
                  {action === "return" && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <span className="bg-yellow-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">
                            6
                          </span>
                          <h3 className="text-sm font-semibold">
                            Detalhes da Devolução
                          </h3>
                        </div>

                        <div className="pl-8 space-y-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium">
                              Motivo da devolução *
                            </Label>
                            <Select
                              value={reasonId}
                              onValueChange={setReasonId}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Selecione o motivo" />
                              </SelectTrigger>
                              <SelectContent>
                                {reasonsForReturn.map((reason) => (
                                  <SelectItem
                                    key={reason.value}
                                    value={reason.value}
                                  >
                                    {reason.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium">
                              Descrição da devolução *
                            </Label>
                            <Textarea
                              value={actionDescription}
                              onChange={(e: any) =>
                                setActionDescription(e.target.value)
                              }
                              placeholder="Descreva quais informações o cidadão deve fornecer..."
                              rows={2}
                              className="resize-none text-sm"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium">
                              Template de mensagem
                            </Label>
                            <Select
                              value={messageTemplateId}
                              onValueChange={handleTemplateChange}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Selecione o template" />
                              </SelectTrigger>
                              <SelectContent>
                                {templates.map((template: any) => (
                                  <SelectItem
                                    key={template.id}
                                    value={template.id}
                                  >
                                    {template.title}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium">
                              Mensagem personalizada
                            </Label>
                            <Textarea
                              value={customMessage}
                              onChange={(e: any) =>
                                setCustomMessage(e.target.value)
                              }
                              placeholder="Escreva uma mensagem clara e educada ao cidadão..."
                              rows={2}
                              className="resize-none text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Campos extras para Arquivar */}
                  {action === "archive" && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <span className="bg-gray-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">
                            6
                          </span>
                          <h3 className="text-sm font-semibold">
                            Detalhes do Arquivamento
                          </h3>
                        </div>

                        <div className="pl-8 space-y-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium">
                              Motivo do arquivamento *
                            </Label>
                            <Select
                              value={reasonId}
                              onValueChange={setReasonId}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Selecione o motivo" />
                              </SelectTrigger>
                              <SelectContent>
                                {reasonsForArchive.map((reason) => (
                                  <SelectItem
                                    key={reason.value}
                                    value={reason.value}
                                  >
                                    {reason.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {reasonId === "DUPLICATE" && (
                            <div className="space-y-1.5 relative protocol-search-container">
                              <Label className="text-xs font-medium">
                                Protocolo da ocorrência original *
                              </Label>
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                  type="text"
                                  value={
                                    protocolSearchTerm || duplicateProtocol
                                  }
                                  onChange={(e) => {
                                    setProtocolSearchTerm(e.target.value);
                                    setShowProtocolSuggestions(true);
                                    if (!e.target.value) {
                                      setDuplicateProtocol("");
                                    }
                                  }}
                                  onFocus={() =>
                                    setShowProtocolSuggestions(true)
                                  }
                                  placeholder="Digite o protocolo para buscar..."
                                  className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-9 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                />
                                {(protocolSearchTerm || duplicateProtocol) && (
                                  <button
                                    onClick={() => {
                                      setProtocolSearchTerm("");
                                      setDuplicateProtocol("");
                                      setShowProtocolSuggestions(false);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                              </div>

                              {/* Sugestões de autocomplete */}
                              {showProtocolSuggestions &&
                                protocolSearchTerm.length >= 3 && (
                                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                                    {isSearchingProtocol ? (
                                      <div className="p-3 text-center text-sm text-gray-500">
                                        <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
                                        Buscando...
                                      </div>
                                    ) : protocolSearchResults &&
                                      protocolSearchResults.length > 0 ? (
                                      protocolSearchResults.map((occ: any) => (
                                        <button
                                          key={occ.id}
                                          onClick={() => {
                                            setDuplicateProtocol(occ.protocol);
                                            setProtocolSearchTerm("");
                                            setShowProtocolSuggestions(false);
                                          }}
                                          className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b last:border-b-0 transition-colors"
                                        >
                                          <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-semibold text-gray-900">
                                                {occ.protocol}
                                              </p>
                                              <p className="text-xs text-gray-600 truncate">
                                                {occ.description}
                                              </p>
                                              <div className="flex items-center gap-2 mt-1">
                                                <Badge
                                                  variant="outline"
                                                  className="text-xs"
                                                >
                                                  {occ.category}
                                                </Badge>
                                                <span className="text-xs text-gray-500">
                                                  {new Date(
                                                    occ.createdAt,
                                                  ).toLocaleDateString("pt-BR")}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </button>
                                      ))
                                    ) : (
                                      <div className="p-3 text-center text-sm text-gray-500">
                                        Nenhuma ocorrência encontrada
                                      </div>
                                    )}
                                  </div>
                                )}

                              {duplicateProtocol && !protocolSearchTerm && (
                                <div className="flex items-center gap-2 mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                  <span className="text-xs text-green-700 font-medium">
                                    Protocolo selecionado: {duplicateProtocol}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium">
                              Descrição do arquivamento *
                            </Label>
                            <Textarea
                              value={actionDescription}
                              onChange={(e: any) =>
                                setActionDescription(e.target.value)
                              }
                              placeholder="Descreva o motivo pelo qual esta ocorrência foi arquivada..."
                              rows={2}
                              className="resize-none text-sm"
                            />
                          </div>

                          {/* <div className="space-y-1.5">
                            <Label className="text-xs font-medium">
                              Template de mensagem
                            </Label>
                            <Select
                              value={messageTemplateId}
                              onValueChange={handleTemplateChange}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Selecione o template" />
                              </SelectTrigger>
                              <SelectContent>
                                {templates.map((template: any) => (
                                  <SelectItem
                                    key={template.id}
                                    value={template.id}
                                  >
                                    {template.title}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div> */}

                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium">
                              Mensagem personalizada
                            </Label>
                            <Textarea
                              value={customMessage}
                              onChange={(e: any) =>
                                setCustomMessage(e.target.value)
                              }
                              placeholder="Escreva uma mensagem clara e educada ao cidadão..."
                              rows={2}
                              className="resize-none text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Botões de Ação */}
              <div className="flex justify-end gap-2 pt-3">
                <Button
                  variant="outline"
                  size="default"
                  onClick={() => navigate("/occurrences")}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirm}
                  size="default"
                  className="cursor-pointer text-white"
                >
                  {allowTriageDecision
                    ? "Confirmar Triagem"
                    : "Salvar Alterações"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Mensagens / Comentários */}
          {occurrence.comments && occurrence.comments.length > 0 && (
            <Card className="bg-white mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Mensagens
                  <Badge variant="secondary" className="ml-auto">
                    {occurrence.comments.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  {occurrence.comments
                    .sort(
                      (a: any, b: any) =>
                        new Date(a.createdAt).getTime() -
                        new Date(b.createdAt).getTime(),
                    )
                    .map((comment: any) => {
                      const isSystemMessage = comment.type === "PUBLIC";
                      const isCitizenMessage =
                        comment.type === "CITIZEN_RESPONSE";
                      const isAdminMessage = comment.author?.role !== "CITIZEN";

                      return (
                        <div
                          key={comment.id}
                          className={`flex ${isAdminMessage ? "justify-end" : "justify-start"} mb-3`}
                        >
                          <div
                            className={`max-w-[75%] rounded-lg p-4 transition-colors ${
                              isAdminMessage
                                ? "bg-emerald-50 hover:bg-emerald-100"
                                : "bg-gray-50 hover:bg-gray-100"
                            }`}
                          >
                            <div className="flex gap-3">
                              <Avatar className="h-9 w-9">
                                <AvatarFallback
                                  className={`font-semibold ${
                                    isAdminMessage
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-blue-100 text-blue-700"
                                  }`}
                                >
                                  {comment.author.profile.fullName
                                    .charAt(0)
                                    .toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-semibold text-sm text-gray-900">
                                      {comment.author.profile.fullName}
                                    </p>
                                    <Badge
                                      variant="secondary"
                                      className="border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs h-5"
                                    >
                                      {comment.author.role === "CITIZEN"
                                        ? "Cidadão"
                                        : "Administrador"}
                                    </Badge>
                                    <span className="text-xs text-gray-500">
                                      {new Date(
                                        comment.createdAt,
                                      ).toLocaleString("pt-BR", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                  {renderMarkdownText(comment.content)}
                                </p>
                                {comment.attachments &&
                                  comment.attachments.length > 0 && (
                                    <div className="mt-2 space-y-2">
                                      {comment.attachments.map(
                                        (attachment: any) => (
                                          <div
                                            key={attachment.id}
                                            className="relative"
                                          >
                                            {attachment.mimeType.startsWith(
                                              "image/",
                                            ) && (
                                              <img
                                                src={`${import.meta.env.VITE_BUCKET_URL}${attachment.key}`}
                                                alt={attachment.fileName}
                                                className="rounded-lg max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                                                onClick={() => {
                                                  window.open(
                                                    `${import.meta.env.VITE_BUCKET_URL}${attachment.key}`,
                                                    "_blank",
                                                  );
                                                }}
                                              />
                                            )}
                                            <p className="text-[10px] mt-1 text-gray-500">
                                              📎 {attachment.fileName} (
                                              {(attachment.size / 1024).toFixed(
                                                1,
                                              )}{" "}
                                              KB)
                                            </p>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        <div className="lg:col-span-1 w-full">
          <div className="space-y-3 w-full">
            <Card className="bg-white w-full">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Ocorrência</CardTitle>
                  <Badge
                    variant={
                      statusConfig[occurrence.status]?.variant || "outline"
                    }
                  >
                    {statusConfig[occurrence.status]?.label ||
                      occurrence.status}
                  </Badge>
                </div>
                <span className="text-lg font-bold text-primary">
                  {protocol}
                </span>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg p-3 border border-primary/20">
                  <p className="text-[10px] font-semibold text-primary uppercase tracking-wide mb-1">
                    Classificação Atual
                  </p>
                  <div className="space-y-0.5">
                    <p className="font-bold text-sm text-gray-900">
                      {occurrence.category.name}
                    </p>
                    <p className="text-xs text-gray-600 flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-primary"></span>
                      {occurrence.subcategory.name}
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <div className="flex items-start gap-2">
                    <FileText className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-500 tracking-wide mb-0.5">
                        Descrição
                      </p>
                      <p className="text-xs leading-relaxed text-gray-700 break-words whitespace-pre-wrap">
                        {renderMarkdownText(occurrence.description)}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5 p-1.5 rounded-lg bg-blue-50">
                      <MapPin className="h-3.5 w-3.5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-gray-500 tracking-wide mb-0.5">
                        Endereço
                      </p>
                      <p className="text-xs text-gray-900 leading-relaxed font-medium">
                        {occurrence.address}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5 p-1.5 rounded-lg bg-green-50">
                      <Calendar className="h-3.5 w-3.5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-gray-500 tracking-wide mb-0.5">
                        Data de Criação
                      </p>
                      <p className="text-xs text-gray-900 font-medium">
                        {new Date(occurrence.createdAt).toLocaleString(
                          "pt-BR",
                          {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card do Cidadão */}
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  Dados do Cidadão
                  {occurrence.isAnonymous && (
                    <Badge variant="outline" className="text-xs border-purple-300 text-purple-700 bg-purple-50">
                      Anônima
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {occurrence.isAnonymous ? (
                  <>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-purple-100 text-purple-700">
                          A
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">Usuário Anônimo</p>
                        <p className="text-sm text-gray-500">Denúncia anônima</p>
                      </div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-700">
                      Esta é uma ocorrência anônima. Os dados pessoais do solicitante não estão disponíveis.
                    </div>
                  </>
                ) : (
                <>
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-emerald-100 text-emerald-700">
                      {occurrence.user.profile.fullName.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {occurrence.user.profile.fullName}
                    </p>
                    <p className="text-sm text-gray-500">Cidadão</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                  <div>
                    <p className="text-xs text-gray-500 font-semibold">CPF</p>
                    <p className="font-medium text-xs">
                      {occurrence.user.profile.cpf.replace(
                        /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
                        "$1.$2.$3-$4",
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-semibold">
                      E-mail
                    </p>
                    <p className="font-medium text-xs break-all">
                      {occurrence.user.email}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-semibold">
                      Telefone
                    </p>
                    <p className="font-medium text-xs">
                      {occurrence.user.profile.phone.replace(
                        /^(\d{2})(\d{5})(\d{4})$/,
                        "($1) $2-$3",
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-semibold">
                      Endereço
                    </p>
                    <p className="font-medium text-xs">
                      {occurrence.user.profile.street},{" "}
                      {occurrence.user.profile.number}
                    </p>
                    <p className="text-[10px] text-gray-600">
                      {occurrence.user.profile.neighborhood}
                    </p>
                    <p className="text-[10px] text-gray-600">
                      {occurrence.user.profile.city}/
                      {occurrence.user.profile.state} - CEP:{" "}
                      {occurrence.user.profile.zipCode.replace(
                        /^(\d{5})(\d{3})$/,
                        "$1-$2",
                      )}
                    </p>
                  </div>
                </div>
                </>
                )}
              </CardContent>
            </Card>

            {/* Mídias Anexadas */}
            {occurrence.media && occurrence.media.length > 0 && (
              <Card className="bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Mídias Anexadas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    {occurrence.media.map((media: any, index: number) => {
                      const mediaUrl = `${import.meta.env.VITE_BUCKET_URL}${media.key}`;
                      const isImage = media.type === "IMAGE";
                      const isVideo = media.type === "VIDEO";
                      const imageIndex = occurrence.media
                        .filter((m: any) => m.type === "IMAGE")
                        .findIndex((m: any) => m.id === media.id);

                      return (
                        <div
                          key={media.id}
                          className="relative group overflow-hidden rounded-lg border hover:shadow-lg transition-all"
                        >
                          {isImage && (
                            <div
                              className="cursor-pointer h-24 relative overflow-hidden"
                              onClick={() => handleViewMedia(imageIndex)}
                            >
                              <img
                                src={mediaUrl}
                                alt="Imagem da ocorrência"
                                className="w-full h-full object-cover bg-white"
                              />
                              <div className="absolute inset-0 bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center pointer-events-none">
                                <ImageIcon className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </div>
                          )}
                          {isVideo && (
                            <div
                              className="cursor-pointer h-24 bg-gray-100 relative overflow-hidden group"
                              onClick={() => handleViewVideo(mediaUrl)}
                            >
                              <video
                                src={mediaUrl}
                                className="w-full h-full object-cover"
                                preload="metadata"
                                playsInline
                                muted
                                onLoadedMetadata={(e) => {
                                  const video = e.target as HTMLVideoElement;
                                  video.currentTime = 1;
                                }}
                              >
                                Seu navegador não suporta reprodução de vídeo.
                              </video>
                              <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-40 transition-opacity flex items-center justify-center pointer-events-none">
                                <div className="bg-white/90 rounded-full p-3 opacity-0 group-hover:opacity-100 transition-all">
                                  <PlayIcon
                                    className="h-8 w-8 text-gray-900"
                                    fill="currentColor"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="p-1.5 bg-white">
                            <p className="text-[10px] text-gray-600 truncate">
                              {media.key.split("/").pop()}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {(media.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Localização */}
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Localização
                </CardTitle>
              </CardHeader>
              <CardContent>
                {coordinates ? (
                  <LocationPicker
                    initialCoordinates={coordinates}
                    onLocationChange={handleLocationChange}
                    height="300px"
                    showOriginalMarker={true}
                  />
                ) : (
                  <div className="flex items-center justify-center h-[300px] bg-gray-100 rounded-lg">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                )}
                {newAddress && (
                  <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-xs font-semibold text-emerald-800 mb-1">
                      Novo endereço selecionado:
                    </p>
                    <p className="text-sm text-emerald-700">{newAddress}</p>

                  </div>
                )}
              </CardContent>
            </Card>

            <Lightbox
              open={lightboxOpen}
              close={() => setLightboxOpen(false)}
              slides={imageSlides}
              index={lightboxIndex}
              styles={{ 
                container: { backgroundColor: "rgba(0, 0, 0, 0.9)", zIndex: 9999 },
                root: { zIndex: 9999 }
              }}
            />

            <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
              <DialogContent
                className="p-0 border-none"
                style={{ width: "1400px", maxWidth: "90vw", height: "auto" }}
              >
                <div
                  className="relative w-full"
                  style={{ aspectRatio: "16/9" }}
                >
                  {selectedVideo && (
                    <video
                      src={selectedVideo}
                      controls
                      autoPlay
                      className="w-full h-full"
                    >
                      Seu navegador não suporta reprodução de vídeo.
                    </video>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </>
  );
};
